import { runAfterPaint } from 'utils/runAfterPaint';

let deferProcessing = false;
const pending: (() => void)[] = [];
let cancelScheduled: (() => void) | undefined;
let dispatching = false;

function dispatch(task: () => void): void {
    try {
        task();
    } catch {
        // A broken third-party tag must not break the UI or strand later events.
    }
}

function stopWaiting(): void {
    cancelScheduled?.();
    cancelScheduled = undefined;
    window.removeEventListener('pagehide', flush);
    document.removeEventListener('visibilitychange', onVisibilityChange);
}

function flush(): void {
    stopWaiting();
    if (dispatching) return;
    dispatching = true;
    try {
        // Hand off before the page freezes/exits. GTM still owns network delivery;
        // this is not a guarantee that a conversion reaches a vendor.
        while (pending.length) dispatch(pending.shift()!);
    } finally {
        dispatching = false;
    }
}

function onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') flush();
}

function scheduleNext(): void {
    if (dispatching || cancelScheduled || !pending.length) return;
    if (document.visibilityState === 'hidden') {
        flush();
        return;
    }
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibilityChange);
    cancelScheduled = runAfterPaint(() => {
        cancelScheduled = undefined;
        dispatching = true;
        dispatch(pending.shift()!);
        dispatching = false;
        if (pending.length) scheduleNext();
        else stopWaiting();
    });
}

/** Enable when gtm.js loads; until then, pushes fill its native array in their original order. */
export function deferGtmProcessing(): void {
    deferProcessing = true;
}

/** One app-owned message per rendering opportunity, in order (including identity changes). */
export function scheduleGtmTask(task: () => void): void {
    if (typeof window === 'undefined') return;
    if (!deferProcessing) {
        dispatch(task);
        return;
    }
    pending.push(task);
    scheduleNext();
}

/** Never wrap dataLayer.push itself: GTM and its tags own their independent callbacks. */
export function pushGtmDataLayer(message: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    // Match the provider's existing shallow snapshot. In particular, deposit and
    // trade callers reuse one payload while changing event_id between conversions.
    const snapshot = { ...message };
    scheduleGtmTask(() => window.dataLayer?.push(snapshot));
}
